import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Input } from 'reactstrap'
import { addBankDetailsApiFun } from '../../API/Services/authService'
import LoaderWrapper from '../../Components/LoaderWrapper/LoaderWrapper'
import { useAlert } from 'react-alert'
import FieldBadge from '../../Components/Common/badge/FieldBadge'
import { fieldBadgeClassName } from '../../helper/helper'

// ⚠️ For testing only. Do NOT ship secret keys to the browser in production.
const PAYSTACK_TOKEN = process.env.REACT_APP_PAYSTACK_TOKEN
const PAYSTACK_BASE = 'https://api.paystack.co'

const showSaveBtn = (user) => {
  if (user?.fieldStatus) {
    const { accountNo, bankName, accountHolderName } = user.fieldStatus
    if (accountNo == 2 && bankName == 2 && accountHolderName == 2) return ''
    return (
      <div className="save-btn-wrapper">
        <button type="submit" className="btn primary-btn w-auto px-5">Save</button>
      </div>
    )
  }
  return ''
}

function BankDetails({ bankDetails, updateData, user }) {
  const alert = useAlert()
  const [loader, setLoader] = useState(false)

  // Paystack data
  const [paystackBanks, setPaystackBanks] = useState([]) // [{code, name}]
  const [banksLoading, setBanksLoading] = useState(false)
  const [resolveStatus, setResolveStatus] = useState('idle') // idle|resolving|success|error
  const [resolvedName, setResolvedName] = useState('')

  // RHF
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    control,
    watch,
  } = useForm({
    defaultValues: {
      bankCode: '',                  // <- new: Paystack bank code
      bankName: bankDetails?.bankName || '',
      holderName: bankDetails?.accountHolderName || '',
      acc: bankDetails?.accountNo || '',
    },
  })

  // Prefill when prop changes
  useEffect(() => {
    setValue('bankName', bankDetails?.bankName || '')
    setValue('holderName', bankDetails?.accountHolderName || '')
    setValue('acc', bankDetails?.accountNo || '')
  }, [bankDetails, setValue])

  // Fetch Paystack banks
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        setBanksLoading(true)
        const res = await fetch(`${PAYSTACK_BASE}/bank?currency=NGN`, {
          headers: { Authorization: `Bearer ${PAYSTACK_TOKEN}`, accept: 'application/json' },
        })
        const json = await res.json()
        const list = Array.isArray(json?.data) ? json.data : []
        if (!cancelled) {
          setPaystackBanks(list.map(b => ({ code: b?.code, name: b?.name })))
        }
      } catch (e) {
        if (!cancelled) alert.error(e?.message || 'Failed to load banks from Paystack')
      } finally {
        if (!cancelled) setBanksLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [])

  // Auto-resolve account name when bankCode + 10-digit account number present
  const accVal = watch('acc')
  const bankCodeVal = watch('bankCode')
  const resolveTimerRef = useRef(null)
  const lastResolveKeyRef = useRef('')

  useEffect(() => {
    const digits = (accVal || '').replace(/\D/g, '')
    setResolvedName(prev => prev) // no-op to keep lint quiet

    if (!bankCodeVal || digits.length !== 10) {
      setResolveStatus('idle')
      return
    }

    const key = `${bankCodeVal}:${digits}`
    if (lastResolveKeyRef.current === key && resolveStatus === 'success') {
      return // already resolved this combination
    }

    if (resolveTimerRef.current) clearTimeout(resolveTimerRef.current)
    resolveTimerRef.current = setTimeout(async () => {
      try {
        setResolveStatus('resolving')
        const url = `${PAYSTACK_BASE}/bank/resolve?account_number=${digits}&bank_code=${encodeURIComponent(bankCodeVal)}`
        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${PAYSTACK_TOKEN}`, accept: 'application/json' },
        })
        const json = await res.json()
        if (json?.status && json?.data?.account_name) {
          const accountName = json.data.account_name
          setResolvedName(accountName)
          setValue('holderName', accountName, { shouldValidate: true, shouldDirty: true })
          setResolveStatus('success')
          lastResolveKeyRef.current = key
        } else {
          setResolvedName('')
          setResolveStatus('error')
        }
      } catch (e) {
        setResolvedName('')
        setResolveStatus('error')
      }
    }, 500) // debounce 500ms

    return () => {
      if (resolveTimerRef.current) clearTimeout(resolveTimerRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accVal, bankCodeVal])

  // Disable logic same as your pattern
  const disabledAccountNo = user?.fieldStatus?.accountNo == 2
  const disabledBank = user?.fieldStatus?.bankName == 2
  const disabledHolder = user?.fieldStatus?.accountHolderName == 2

  const handleBankChange = (e, onChange) => {
    const code = e.target.value
    onChange(code) // sets bankCode
    const selected = paystackBanks.find(b => b.code === code)
    // Keep a human-readable name for submission
    setValue('bankName', selected?.name || '')
    // Clear previous resolution if any
    setResolvedName('')
    setResolveStatus('idle')
  }

  const handleBankDetails = (formData) => {
    setLoader(true)

    // If we auto-resolved, prefer that. Otherwise, use whatever is in holderName input.
    const payload = {
      bankName: formData.bankName,                 // human-readable bank name
      accountNo: (formData.acc || '').replace(/\D/g, ''),
      accountHolderName: resolvedName || formData.holderName || '',
    }

    addBankDetailsApiFun(payload)
      .then(response => {
        setLoader(false)
        if (response?.data?.status === 'OK' || response?.data?.statusCode === 200) {
          alert.success(response?.data?.message || 'Bank details updated')
          updateData?.('4')
        } else {
          alert.error(response?.data?.message || 'Update failed')
          console.log('Error response-->', response)
        }
      })
      .catch(error => {
        console.log(error)
        setLoader(false)
        alert.error(error?.message || 'Request failed')
      })
  }

  return (
    <>
      <div className="fill-details-wrapper">
        <div className="fill-details-heading">Add Bank Details</div>
        <div className="row">
          <div className="col-xl-8 col-lg-10">
            <form className="" onSubmit={handleSubmit(handleBankDetails)}>
              <div className="form-wrapper">

                {/* Account Number */}
                <div className="input-wrapper">
                  <label className="input-label-profile" htmlFor="acc">
                    Bank Account Number: <FieldBadge status={user?.fieldStatus?.accountNo} />
                  </label>
                  <input
                    disabled={!!disabledAccountNo}
                    type="text"
                    inputMode="numeric"
                    className={`input-box-profile form-control ${errors.acc ? 'not-valid' : ''} ${fieldBadgeClassName(user?.fieldStatus?.accountNo)}`}
                    {...register('acc', {
                      required: 'Please enter account number',
                      validate: (v) => {
                        const n = (v || '').replace(/\D/g, '')
                        if (n.length !== 10) return 'Account number must be 10 digits'
                        return true
                      },
                      onChange: (e) => {
                        // keep only digits while typing
                        const onlyDigits = e.target.value.replace(/\D/g, '')
                        setValue('acc', onlyDigits, { shouldValidate: true, shouldDirty: true })
                      },
                    })}
                    id="acc"
                    placeholder="Bank Account Number"
                  />
                  {errors.acc && <small className="error-msg">{errors.acc.message}</small>}
                </div>

                {/* Bank (from Paystack) */}
                <div className="input-wrapper">
                  <label className="input-label-profile" htmlFor="bank-code">
                    Select Bank: <FieldBadge status={user?.fieldStatus?.bankName} />
                  </label>
                  <Controller
                    control={control}
                    name="bankCode"
                    rules={{ required: 'Please select your bank' }}
                    render={({ field: { onChange, value } }) => (
                      <Input
                        disabled={!!disabledBank || banksLoading}
                        type="select"
                        className={`input-box-profile form-control ${errors.bankCode ? 'not-valid' : ''} ${fieldBadgeClassName(user?.fieldStatus?.bankName)}`}
                        id="bank-code"
                        value={value || ''}
                        onChange={(e) => handleBankChange(e, onChange)}
                      >
                        <option value="" disabled>
                          {banksLoading ? 'Loading banks…' : 'Select Bank'}
                        </option>
                        {paystackBanks.map((bank) => (
                          <option value={bank.code} key={bank.code}>
                            {bank.name}
                          </option>
                        ))}
                      </Input>
                    )}
                  />
                  {errors.bankCode && <small className="error-msg">{errors.bankCode.message}</small>}
                </div>

                {/* Account Holder Name (auto-filled) */}
                <div className="input-wrapper">
                  <label className="input-label-profile" htmlFor="holder-name">
                    Account Holder Name: <FieldBadge status={user?.fieldStatus?.accountHolderName} />
                  </label>

                  <div className="d-flex gap-2 align-items-center">
                    <input
                      disabled={!!disabledHolder}
                      type="text"
                      className={`input-box-profile form-control ${errors.holderName ? 'not-valid' : ''} ${fieldBadgeClassName(user?.fieldStatus?.accountHolderName)}`}
                      {...register('holderName', { required: 'Please enter account holder name' })}
                      id="holder-name"
                      placeholder="Account Holder Name"
                    />

                    {/* Small inline status pill for resolve */}
                    {resolveStatus === 'resolving' && (
                      <span className="badge bg-secondary">Resolving…</span>
                    )}
                    {resolveStatus === 'success' && (
                      <span className="badge bg-success">Verified</span>
                    )}
                    {resolveStatus === 'error' && (
                      <span className="badge bg-danger">Not matched</span>
                    )}
                  </div>

                  {errors.holderName && (
                    <small className="error-msg">{errors.holderName.message}</small>
                  )}

                  {/* If we got a resolved name but the input differs, hint it */}
                  {resolvedName &&
                    resolvedName.trim().toLowerCase() !==
                      (watch('holderName') || '').trim().toLowerCase() && (
                      <small className="text-muted">
                        Suggested from bank: <strong>{resolvedName}</strong>{' '}
                        <button
                          type="button"
                          className="btn btn-link p-0 ms-1"
                          onClick={() => setValue('holderName', resolvedName, { shouldValidate: true, shouldDirty: true })}
                        >
                          Use this
                        </button>
                      </small>
                    )}
                </div>
              </div>

              {user ? showSaveBtn(user) : ''}
            </form>
          </div>
        </div>
      </div>

      {loader ? <LoaderWrapper /> : ''}
    </>
  )
}

export default BankDetails
